import { useState, useMemo, useCallback } from "react";

// Null/blank values always sort to the bottom regardless of direction.
const isNil = (v) => v === null || v === undefined || v === "";

const compareValues = (a, b) => {
    const aNil = isNil(a);
    const bNil = isNil(b);
    if (aNil && bNil) return 0;
    if (aNil) return 1;
    if (bNil) return -1;

    if (typeof a === "number" && typeof b === "number") {
        return a - b;
    }

    if (a instanceof Date && b instanceof Date) {
        return a.getTime() - b.getTime();
    }

    // Numeric-aware, case-insensitive string comparison. Handles numbers stored
    // as strings ("2" < "10") and mixed values without extra per-column config.
    return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
    });
};

/**
 * Reusable client-side sorting for the hand-rolled Reactstrap tables in this app.
 *
 * @param {Array} items            The rows to sort (already filtered).
 * @param {Object} [options]
 * @param {string|null} [options.key]        Initial sort column key (null = unsorted).
 * @param {"asc"|"desc"} [options.direction] Initial direction (default "asc").
 * @param {Object} [options.getters]         Map of key -> (row) => value, for
 *                                           computed columns (e.g. joined labels).
 * @returns {{ items: Array, sortConfig: Object|null, requestSort: Function,
 *             sortKey: string|null, sortDirection: string|null }}
 */
export default function useSortableData(items, options = {}) {
    const { key = null, direction = "asc", getters = {} } = options;
    const [sortConfig, setSortConfig] = useState(key ? { key, direction } : null);

    const sorted = useMemo(() => {
        const list = Array.isArray(items) ? [...items] : [];
        if (!sortConfig || !sortConfig.key) return list;
        const getter = getters[sortConfig.key] || ((row) => (row ? row[sortConfig.key] : undefined));
        list.sort((a, b) => {
            const cmp = compareValues(getter(a), getter(b));
            return sortConfig.direction === "asc" ? cmp : -cmp;
        });
        return list;
        // getters is expected to be stable or inline-cheap; a resort per render is fine.
    }, [items, sortConfig, getters]);

    // Clicking a new column starts at ascending; clicking the active column flips.
    const requestSort = useCallback((columnKey) => {
        setSortConfig((prev) => {
            if (prev && prev.key === columnKey) {
                return { key: columnKey, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key: columnKey, direction: "asc" };
        });
    }, []);

    return {
        items: sorted,
        sortConfig,
        requestSort,
        sortKey: sortConfig ? sortConfig.key : null,
        sortDirection: sortConfig ? sortConfig.direction : null,
    };
}
